/**
 * @memberof module:util
 * @function
 * @name originDischargeSectionArr
 * @description Get origin and discharge charge array from a single rate card.
 * While getting destination charge array, If delivery checkbox is NOT checked, then add destination extra charges to port of discharge charge array (i.e. property "port_fees_to").
 *
 * @param {string} mode - It's the mode either origin or discharge
 * @param {Object} itemJson - Single rate card
 * @param {boolean} [isDeliveryChecked=undefined] - Whether delivery option is checked in view-details section.
 * @param {Object} [options={}] - Additional options like equipment type.
 * @returns {Array}
 */

export const originDischargeSectionArr = (
  mode,
  itemJson,
  isDeliveryChecked = undefined,
  options = {}
) => {
  const { equipmentSizeType } = options || {};
  const normalizedType = normalizeEquipmentSizeType(equipmentSizeType);
  const perEquipmentCharges = getChargesByEquipment(mode, itemJson);

  const specificCharges = normalizedType ? perEquipmentCharges?.[normalizedType] : null;
  const hasSpecificCharges = specificCharges?.length > 0;
  const hasAnyEquipmentCharges = perEquipmentCharges && Object.keys(perEquipmentCharges).length > 0;

  // Equipment charges exist but none match the requested type → show mode default (e.g. Variable Charge)
  if (hasAnyEquipmentCharges && normalizedType && !hasSpecificCharges) {
    const defaultSection = getDefaultSection(mode);
    return [...((Array.isArray(itemJson[defaultSection]) && itemJson[defaultSection]) || [])];
  }

  if (hasAnyEquipmentCharges && (!normalizedType || hasSpecificCharges)) {
    const appendEquipmentCode = (charges = [], type) =>
      charges.map((charge) => {
        if (!Array.isArray(charge)) return charge;
        const entry = [...charge];
        if (entry.length > 6) {
          entry[6] = entry[6] || type;
        } else {
          entry.push(type);
        }
        return entry;
      });

    const equipmentCharges = hasSpecificCharges
      ? appendEquipmentCode(specificCharges, normalizedType)
      : Object.entries(perEquipmentCharges || {}).flatMap(([type, charges]) =>
          appendEquipmentCode(charges, type)
        );

    const chargesWithEquipment = equipmentCharges;

    if (!isDeliveryChecked && mode === DISCHARGE) {
      let extraChargeArr = itemJson[DISCHARGE_EXTRA_CHARGE_JSON_KEY];
      extraChargeArr = [
        ...(Array.isArray(extraChargeArr) && extraChargeArr.length !== 0
          ? extraChargeArr
          : []),
      ];
      extraChargeArr.forEach((item) => chargesWithEquipment.push(item));
    }

    return chargesWithEquipment;
  }

  const modeSection = getSection(mode);
  let sectionArr = [
    ...((Array.isArray(itemJson[modeSection]) && itemJson[modeSection]) || []),
  ];

  if (!isDeliveryChecked && mode === DISCHARGE) {
    let extraChargeArr = itemJson[DISCHARGE_EXTRA_CHARGE_JSON_KEY];
    extraChargeArr = [
      ...(Array.isArray(extraChargeArr) && extraChargeArr.length !== 0
        ? extraChargeArr
        : []),
    ];
    extraChargeArr.map((item) => sectionArr.push(item));
  }

  return sectionArr;
};

// get section
const getSection = (mode) => {
  if (mode === ORIGIN) return ORIGIN_JSON_KEY;
  if (mode === DISCHARGE) return DISCHARGE_JSON_KEY;
};

const getDefaultSection = (mode) => {
  if (mode === ORIGIN) return ORIGIN_DEFAULT_KEY;
  if (mode === DISCHARGE) return DISCHARGE_DEFAULT_KEY;
};

const getChargesByEquipment = (mode, itemJson) => {
  if (mode === ORIGIN) {
    return itemJson?.originChargesByEquipment;
  }
  if (mode === DISCHARGE) {
    return itemJson?.destinationChargesByEquipment;
  }
  return null;
};

const normalizeEquipmentSizeType = (equipmentSizeType = '') => {
  if (!equipmentSizeType) return '';
  const upper = equipmentSizeType.toUpperCase();
  if (upper === '40HQ') return '40HC';
  if (upper === '45HQ') return '45HC';
  if (upper === '20DV' || upper === '20GP') return '20ST';
  return upper;
};

// mode
const ORIGIN = 'origin';
const DISCHARGE = 'discharge';

// for json, mode section
const ORIGIN_JSON_KEY = 'port_fees_from';
const ORIGIN_DEFAULT_KEY = 'port_fees_from_default';
const DISCHARGE_JSON_KEY = 'port_fees_to';
const DISCHARGE_DEFAULT_KEY = 'port_fees_to_default';

// destination or discharge extra charges
const DISCHARGE_EXTRA_CHARGE_JSON_KEY = 'destination_extra_charges';
